import { dilutedRewardAprBps } from '@/lib/loops'
import { toNumber } from '@/lib/utils'

const YEAR_SECONDS = 365 * 24 * 60 * 60

/// The deposit size every cold-start estimate is quoted on. $10,000 is large enough that the
/// quoted rate is reachable rather than a dust artifact, and small enough to be a plausible first
/// deposit. One constant shared by Earn, Markets, Vaults and the Multiply reference basis.
export const EST_REFERENCE_USD = 10_000

/// Cold-start APY estimate for an empty (or near-empty) farm, from its UNGOVERNED budget bounded
/// by its APR-governor ceiling.
///
/// The effective rate the UI normally reads is zero on an empty farm BY DESIGN - the governor
/// accrues `min(budget, capBps x farmTvl)`, and an empty farm's cap term is zero. True, but it
/// makes exactly the markets that most need a first depositor look like they pay nothing, when
/// each carries a real budgeted stream the first deposit would start collecting, capped at the
/// governor's ceiling. This mirrors that same min() for a hypothetical $10,000 deposit: every
/// input is real (today's budget, today's HORIZON price, the on-chain cap) and the one assumption
/// is stated by the caller's label.
///
/// Returns 0 whenever the market already shows a real APY, the HORIZON price is missing, or the
/// farm has no budget - a deliberately unincentivized listing keeps its honest 0% instead of an
/// invented number.
export function coldStartAprBps({ budgetPerSecond, poolUsd, capBps, horizonUsd, hasHorizonPrice, liveAprBps = 0 }) {
  if (!hasHorizonPrice || liveAprBps > 0) return 0
  const budgetUsdPerYear = toNumber(budgetPerSecond ?? 0n) * YEAR_SECONDS * horizonUsd
  const raw = dilutedRewardAprBps(budgetUsdPerYear, poolUsd, EST_REFERENCE_USD)
  if (raw <= 0) return 0
  return capBps > 0 ? Math.min(raw, capBps) : raw
}

/// The supply-side estimate for a reserve, packaged for the three surfaces that show it.
export function coldStartSupplyAprBps(reserve, horizonUsd, hasHorizonPrice) {
  return coldStartAprBps({
    budgetPerSecond: reserve.supplyHorizonBudgetPerSecond,
    poolUsd: reserve.totalSupplied * reserve.priceUsd,
    capBps: reserve.supplyAprCapBps,
    horizonUsd, hasHorizonPrice,
    liveAprBps: reserve.totalSupplyAprBps,
  })
}
